import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { AuthService } from '../../../auth/services/auth.service';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast/toast.service';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { throwError, from } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private excludedRoutes = [
    'assets',
    '/auth/login',
    '/create-password-recover',
    '/users/password-recover',
    '/finalize-registration',
    '/auth/sign-up',
    '/term-of-use-active',
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const isExcludedRoute = this.excludedRoutes.some((route) =>
      req.url.includes(route),
    );

    if (isExcludedRoute) {
      return next.handle(req);
    }

    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        let account = this.msalService.instance.getActiveAccount();
        if (!account) {
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            account = accounts[0];
            this.msalService.instance.setActiveAccount(account);
          } else {
            this.router.navigate(['/auth/login']);
            return next.handle(req);
          }
        }

        const tokenRequest = {
          scopes: [environment.msApiScope],
          account: account,
        };

        return from(this.msalService.acquireTokenSilent(tokenRequest)).pipe(
          switchMap((response) => {
            const clonedReq = req.clone({
              headers: req.headers.set(
                'Authorization',
                `Bearer ${response.accessToken}`,
              ),
            });
            return next.handle(clonedReq);
          }),
          catchError((error: any) => {
            if (error instanceof InteractionRequiredAuthError) {
              this.msalService.loginRedirect();
              return throwError(() => error);
            }

            if (error instanceof HttpErrorResponse) {
              if (error.status === 401) {
                this.authService.clearLocalStorage();
                const errorMessage = error.error.message || 'Login expirado';
                this.toastService.showError(errorMessage);
                this.router.navigate(['/auth/login']);
              }

              if (error.status === 403) {
                const errorMessage = error.error.message || 'Não autorizado';
                this.toastService.showError(errorMessage);
                this.router.navigate(['/home']);
              }
            }

            return throwError(() => error);
          }),
        );
      }),
    );
  }
}
